import React, { useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Exporting from "highcharts/modules/exporting";
import ExportData from "highcharts/modules/export-data";
import { formatDate } from "./outageLoad.constants";

const initHighchartsModule = (moduleFactory) => {
  if (typeof moduleFactory === "function") {
    moduleFactory(Highcharts);
    return;
  }

  if (moduleFactory && typeof moduleFactory.default === "function") {
    moduleFactory.default(Highcharts);
  }
};

const createRenderHandler = (events, chartMaxDay) => function renderChart() {
  const chart = this;
  const xAxis = chart.xAxis[0];
  const yAxis = chart.yAxis[0];
  const plotLeft = chart.plotLeft;
  const plotTop = chart.plotTop;
  const plotRight = plotLeft + chart.plotWidth;
  const plotBottom = plotTop + chart.plotHeight;

  if (chart.customOutageBars) {
    chart.customOutageBars.forEach((shape) => shape.destroy());
  }
  chart.customOutageBars = [];

  const dayPixelWidth = chart.plotWidth / Math.max(1, chartMaxDay);
  const rectWidth = Math.max(2, Math.min(6, dayPixelWidth * 0.75));

  events.forEach((event) => {
    const endHour = Math.min(24, event.startHour + event.duration);
    const x = xAxis.toPixels(event.dayOfYear);
    const yStart = yAxis.toPixels(event.startHour);
    const yEnd = yAxis.toPixels(endHour);
    const top = Math.min(yStart, yEnd);
    const heightPx = Math.max(1, Math.abs(yEnd - yStart));

    if (
      Number.isFinite(x) &&
      Number.isFinite(top) &&
      Number.isFinite(heightPx) &&
      x >= plotLeft - rectWidth &&
      x <= plotRight + rectWidth &&
      top <= plotBottom &&
      top + heightPx >= plotTop
    ) {
      const rect = chart.renderer
        .rect(x - rectWidth / 2, top, rectWidth, heightPx, 1)
        .attr({
          fill: "gray",
          stroke: "gray",
          "stroke-width": 0,
          opacity: 0.85,
          zIndex: 2,
        })
        .add();
      chart.customOutageBars.push(rect);
    }
  });
};

const formatTimeAxisLabel = function formatTimeLabel() {
  return `${String(Math.floor(this.value)).padStart(2, "0")}:00`;
};

const createDayAxisLabelFormatter = (formatDay) => function formatDayLabel() {
  return formatDay(this.value);
};

const createTooltipFormatter = (formatHourToHHmm, formatDurationToHoursMinutes, formatDay) => function formatTooltip() {
  return `<b>Event</b><br/>Date: ${formatDay(this.point.x)}<br/>Start: ${formatHourToHHmm(this.point.startHour)}<br/>End: ${formatHourToHHmm(this.point.endHour)}<br/>Duration: ${formatDurationToHoursMinutes(this.point.durationMinutes)}`;
};


initHighchartsModule(Exporting);
initHighchartsModule(ExportData);

/**
 * YearlyTimelineChart - Visualizes outage events using Highcharts
 * X-axis: Days of year (1-365/366 based on year)
 * Y-axis: Time of day (0-24 hours)
 */
export const YearlyTimelineChart = ({
  events = [],
  height = 350,
  isDarkMode = false,
  chartYear,
  windowStartUtcMs,
  dateFormat,
  maxDaysInYear,
}) => {
  const chartRef = useRef(null);
  const getDaysInYear = (year) => {
    if (!Number.isFinite(year)) return 365;
    const start = Date.UTC(year, 0, 1, 0, 0, 0, 0);
    const end = Date.UTC(year + 1, 0, 1, 0, 0, 0, 0);
    return Math.round((end - start) / (24 * 60 * 60 * 1000));
  };
  const resolvedYear = Number.isFinite(chartYear)
    ? chartYear
    : new Date().getUTCFullYear();
  const resolvedStartMs = Number.isFinite(windowStartUtcMs)
    ? windowStartUtcMs
    : Date.UTC(resolvedYear, 0, 1, 0, 0, 0, 0);
  const resolvedMaxDays = Number.isFinite(maxDaysInYear)
    ? maxDaysInYear
    : getDaysInYear(resolvedYear);
  const maxDayInData = Math.max(1, ...events.map((event) => event.dayOfYear || 1));
  const chartMaxDay = Math.max(resolvedMaxDays, maxDayInData);

  const dayToDate = (day) => new Date(resolvedStartMs + (Math.round(day) - 1) * 24 * 60 * 60 * 1000);
  const formatDay = (day) => formatDate(dayToDate(day), dateFormat, false);

  const getChart = () => chartRef.current?.chart;

  const handleZoomIn = () => {
    const axis = getChart()?.xAxis?.[0];
    if (!axis) return;

    const min = Number.isFinite(axis.min) ? axis.min : 1;
    const max = Number.isFinite(axis.max) ? axis.max : chartMaxDay;
    const range = max - min;
    if (range <= 1) return;

    const newRange = Math.max(1, range * 0.5);
    const center = (min + max) / 2;
    let nextMin = center - newRange / 2;
    let nextMax = center + newRange / 2;

    if (nextMin < 1) {
      nextMax += 1 - nextMin;
      nextMin = 1;
    }
    if (nextMax > chartMaxDay) {
      nextMin -= nextMax - chartMaxDay;
      nextMax = chartMaxDay;
    }

    axis.setExtremes(Math.max(1, nextMin), Math.min(chartMaxDay, nextMax), true, false);
  };

  const handleZoomOut = () => {
    const axis = getChart()?.xAxis?.[0];
    if (!axis) return;

    const min = Number.isFinite(axis.min) ? axis.min : 1;
    const max = Number.isFinite(axis.max) ? axis.max : chartMaxDay;
    const range = max - min;
    const fullRange = chartMaxDay - 1;
    if (range >= fullRange) {
      axis.setExtremes(1, chartMaxDay, true, false);
      return;
    }

    const newRange = range * 2;
    const center = (min + max) / 2;
    let nextMin = center - newRange / 2;
    let nextMax = center + newRange / 2;

    if (nextMin < 1) {
      nextMax += 1 - nextMin;
      nextMin = 1;
    }
    if (nextMax > chartMaxDay) {
      nextMin -= nextMax - chartMaxDay;
      nextMax = chartMaxDay;
    }

    axis.setExtremes(Math.max(1, nextMin), Math.min(chartMaxDay, nextMax), true, false);
  };

  const handleResetZoom = () => {
    const axis = getChart()?.xAxis?.[0];
    if (!axis) return;
    axis.setExtremes(1, chartMaxDay, true, false);
  };

  const handleDownloadPng = () => {
    const chart = getChart();
    if (!chart || typeof chart.exportChart !== "function") return;
    chart.exportChart({ type: "image/png" });
  };

  const handleDownloadCsv = () => {
    if (!Array.isArray(events) || events.length === 0) return;

    const csvEscape = (value) => {
      const str = String(value ?? "");
      if (/[\",\n\r]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
      return str;
    };

    const toDateFromDay = (year, dayOfYear) => dayToDate(dayOfYear);

    const rows = [];
    rows.push(["Event", "Year", "DayOfYear", "DateUTC", "Start", "End", "DurationMinutes"].join(","));

    const sortedEvents = [...events].sort((a, b) => {
      if (a.dayOfYear !== b.dayOfYear) return a.dayOfYear - b.dayOfYear;
      return a.startHour - b.startHour;
    });

    sortedEvents.forEach((event, idx) => {
      const day = Math.max(1, Math.min(chartMaxDay, Math.round(event.dayOfYear || 1)));
      const dateUtc = toDateFromDay(resolvedYear, day).toISOString().slice(0, 10);
      const start = formatHourToHHmm(event.startHour);
      const end = formatHourToHHmm(Math.min(24, (event.startHour || 0) + (event.duration || 0)));
      const durationMinutes = Math.max(0, Math.round((event.duration || 0) * 60));

      rows.push(
        [idx + 1, resolvedYear, day, dateUtc, start, end, durationMinutes]
          .map(csvEscape)
          .join(",")
      );
    });

    const csvContent = `\uFEFF${rows.join("\n")}`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `outage-timeline-${resolvedYear}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const formatHourToHHmm = (hourFloat) => {
    const totalMinutes = Math.round((hourFloat || 0) * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  };

  const formatDurationToHoursMinutes = (minutesValue) => {
    const totalMinutes = Math.max(0, Math.round(minutesValue || 0));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours} h ${String(minutes).padStart(2, "0")} min`;
  };

  // Add scatter points for tooltip interaction
  const scatterData = events.map((event) => {
    const startHour = event.startHour;
    const endHour = event.startHour + event.duration;
    const duration = endHour - startHour;
    const durationMinutes = Math.round(duration * 60);
    return {
      x: event.dayOfYear,
      y: (startHour + endHour) / 2,
      startHour: startHour,
      endHour: endHour,
      duration: duration,
      durationMinutes: durationMinutes
    };
  });

  const chartOptions = {
    chart: {
      type: "scatter",
      height: height,
      panning: {
        enabled: true,
        type: "x",
      },
      panKey: null,
      resetZoomButton: {
        theme: {
          display: "none",
        },
      },
      backgroundColor: "transparent",
      plotBackgroundColor: "transparent",
      style: {
        fontFamily: "Arial, sans-serif"
      },
      events: {
        render: createRenderHandler(events, chartMaxDay),
      }
    },
    title: null,
    xAxis: {
      title: {
        text: `Date (${formatDay(1)} - ${formatDay(chartMaxDay)})`,
        style: {
          color: isDarkMode ? "#d5d7e0" : "#333",
          fontSize: "12px"
        }
      },
      type: "linear",
      min: 1,
      max: chartMaxDay,
      tickInterval: 30,
      gridLineWidth: 1,
      gridLineColor: isDarkMode ? "#444" : "#e0e0e0",
      labels: {
        formatter: createDayAxisLabelFormatter(formatDay),
        style: {
          color: isDarkMode ? "#a0a3b1" : "#666",
          fontSize: "11px"
        }
      }
    },
    yAxis: {
      title: {
        text: "Time of Day (Hours)",
        style: {
          color: isDarkMode ? "#d5d7e0" : "#333",
          fontSize: "12px"
        }
      },
      min: 0,
      max: 24,
      tickInterval: 6,
      gridLineColor: isDarkMode ? "#444" : "#e0e0e0",
      labels: {
        style: {
          color: isDarkMode ? "#a0a3b1" : "#666",
          fontSize: "11px"
        },
        formatter: formatTimeAxisLabel,
      }
    },
    plotOptions: {
      scatter: {
        marker: {
          radius: 4,
          fillColor: "gray",
          lineWidth: 0,
          states: {
            hover: {
              fillColor: "gray",
              radius: 6
            }
          }
        }
      },
      series: {
        states: {
          hover: {
            color: "gray",
            opacity: 1
          }
        }
      }
    },
    exporting: {
      enabled: false,
    },
    legend: {
      enabled: false
    },
    tooltip: {
      formatter: createTooltipFormatter(formatHourToHHmm, formatDurationToHoursMinutes, formatDay),
      backgroundColor: isDarkMode ? "rgba(35, 37, 38, 0.95)" : "rgba(255, 255, 255, 0.95)",
      borderColor: isDarkMode ? "#555" : "#ddd",
      style: {
        color: isDarkMode ? "#d5d7e0" : "#333",
        fontSize: "11px"
      }
    },
    credits: {
      enabled: false
    },
    series: [
      {
        name: "Outage Events",
        type: "scatter",
        data: scatterData,
        marker: {
          radius: 4,
          fillColor: "gray",
          lineColor: "gray",
          lineWidth: 0,
          enabled: true,
          states: {
            hover: {
              radius: 6,
            },
          },
        },
        enableMouseTracking: true,
        states: {
          hover: {
            marker: {
              enabled: true
            }
          }
        }
      }
    ]
  };

  return (
    <div className={`outage-chart-wrapper ${isDarkMode ? "dark" : "light"}`} style={{ width: "100%", height: `${height}px` }}>
      <div className="outage-chart-toolbar">
        <button className="outage-chart-btn" aria-label="outage-chart-zoom-in" title="Zoom in" onClick={handleZoomIn}>
          +
        </button>
        <button className="outage-chart-btn" aria-label="outage-chart-zoom-out" title="Zoom out" onClick={handleZoomOut}>
          -
        </button>
        <button className="outage-chart-btn" aria-label="outage-chart-reset" title="Reset zoom" onClick={handleResetZoom}>
          Reset
        </button>
        <select
          className="outage-chart-btn outage-download-select"
          title="Download"
          defaultValue=""
          onChange={(e) => {
            const value = e.target.value;
            if (value === "png") handleDownloadPng();
            if (value === "csv") handleDownloadCsv();
            e.target.selectedIndex = 0;
          }}
        >
          <option value="" disabled hidden>
            Download
          </option>
          <option value="png">PNG</option>
          <option value="csv">CSV</option>
        </select>
      </div>
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
        ref={chartRef}
      />
    </div>
  );
};

export default YearlyTimelineChart;
